import type {
  MiddlewareAPI,
  PayloadAction,
  Action as ReduxAction,
} from '@reduxjs/toolkit';
import type { CancelTokenSource } from 'axios';

import type { EndpointRequest } from '@iconik-io/iconik-api/src/client/request';
import type { EndpointResponse } from '@iconik-io/iconik-api/src/client/response';

import type { AuthState } from './auth/types';
import type { CustomActionsState } from './custom_actions/types';
import type { EntitiesState } from './entities/types';
import type { EntitiesIndexState } from './entities_index/types';
import type { EntitiesMetaState } from './entities_meta/slice';
import type { MessagesState } from './messages/types';
import type { NormalizedDataInjection } from './middlewares/http_normalizer/types';
import type { ModalsState } from './modals/types';

export type BaseAppState = {
  auth: AuthState;
  entities: EntitiesState;
  entitiesIndex: EntitiesIndexState;
  entitiesMeta: EntitiesMetaState;
  messages: MessagesState;
  modals: ModalsState;
  customActions: CustomActionsState;
};

export type EndpointRequestWeb = EndpointRequest<{
  canRefresh?: boolean;
  cancellable?: boolean;
  cancelKey?: string;
  cancel?: CancelTokenSource;
  skipNormalizer?: boolean;
  skipLoginRedirect?: boolean;
  keepCredentialsOnUnauthorized?: boolean;
  ignoreShareAuth?: boolean;
}>;

export type AppAction =
  | EndpointRequestWeb
  | FunctionWithArgs
  | PayloadAction
  | ReduxAction;

type FunctionWithArgs = (...args: any[]) => any;

export type ActionResult<A extends AppAction> = A extends EndpointRequest
  ? Promise<EndpointResponse<A, NormalizedDataInjection>>
  : A extends FunctionWithArgs
    ? ReturnType<A>
    : A;

export type AppDispatch = <Action extends AppAction>(
  action: Action,
) => ActionResult<Action>;

type AppDispatchStatic<Action extends AppAction> = (
  action: Action,
) => ActionResult<Action>;

export type AppMiddlewareAPI<AppState extends BaseAppState = BaseAppState> =
  MiddlewareAPI<AppDispatchStatic<AppAction>, AppState>;

export type AppMiddleware<
  AppState extends BaseAppState = BaseAppState,
  Action extends AppAction = AppAction,
> = (
  api: AppMiddlewareAPI<AppState>,
) => (next: AppDispatch) => (action: AppAction) => ActionResult<Action>;

type AppThunk<ReturnType> = (
  dispatch: AppDispatch,
  getState: () => BaseAppState,
) => ReturnType;

export function createAppThunk<Args extends unknown[], ReturnType>(
  thunk: (...args: Args) => AppThunk<ReturnType>,
) {
  return thunk;
}
